import type { JSX, Signal } from '@builder.io/qwik';
import {
  component$,
  useContext,
  useContextProvider,
  useVisibleTask$,
} from '@builder.io/qwik';
import type { ArticleInterface } from '~/types/AppData/PageContent/ArticleInterface';
import ArticleBuilder from '~/components/Layout/Builders/ArticleBuilder';
import FiniteScroll from '~/components/Layout/Body/FiniteScroll';
import { AppDataContext } from '~/contexts/AppDataContext';
import { getTemplate } from '~/utilities/getTemplate';
import { ArticleContext } from '~/contexts/ArticleContext';
import { FINITE_SCROLL_NUM } from '~/constants/FINITE_SCROLL';
import { GA } from '~/utilities/GA';
import { isServer } from '@builder.io/qwik/build';
import { pageBuilderTemplate } from '~/constants/pageBuilderTemplate';
import PageBuilder from '~/components/Layout/Builders/PageBuilder';

type BodyType = {
  finiteScrollIndex: number;
  article?: ArticleInterface;
  finiteScrollStarts: Signal;
};

export default component$<BodyType>(
  ({ finiteScrollIndex, article, finiteScrollStarts }): JSX.Element => {
    const appData = useContext(AppDataContext);
    const isPageBuilder = appData.payloadToSSR.template === pageBuilderTemplate;

    useContextProvider(ArticleContext, {
      ...article,
      finiteScrollNumber: finiteScrollIndex,
    });

    // Load on page load
    // eslint-disable-next-line qwik/no-use-visible-task
    useVisibleTask$(() => {
      if (isServer) {
        return;
      }

      const GoogleAnalytics = new GA(article, appData.payloadToSSR);

      if (finiteScrollIndex > 0) {
        GoogleAnalytics.pushPageViewEvent({
          event: 'GTM data ready to GA finite scroll',
          eventLabel: 'page view',
          eventAction: 'Pageview',
          isSlideshow: false,
          finiteScrollIndex: finiteScrollIndex,
        });
      } else if (appData.payloadToSSR.pageContent.slideshow?.currentSlide) {
        GoogleAnalytics.pushPageViewEvent({
          event: 'GTM data ready to GA - Slideshow',
          eventLabel: 'Slideshow page view',
          eventAction: 'Slideshow Pageview',
          isSlideshow: true,
          finiteScrollIndex: finiteScrollIndex,
          moreProperties: {
            slideNumber:
              appData.payloadToSSR.pageContent.slideshow.currentSlide,
          },
        });
      } else {
        GoogleAnalytics.pushPageViewEvent({
          event: 'GTM data ready to GA',
          eventLabel: 'page view',
          eventAction: 'Pageview',
          isSlideshow: false,
          finiteScrollIndex: finiteScrollIndex,
        });
      }
    });

    return (
      <>
        {isPageBuilder && appData.payloadToSSR.pageBuilderContent && (
          <div class='mt-32'>
            <PageBuilder items={appData.payloadToSSR.pageBuilderContent} />
          </div>
        )}

        {!isPageBuilder && <ArticleBuilder items={getTemplate(appData)} />}

        {/* Stop loading the FiniteScroll component once we've reached NUM_OF_ARTICLES */}
        {!isPageBuilder && finiteScrollIndex < FINITE_SCROLL_NUM - 1 && (
          <FiniteScroll
            finiteScrollIndex={finiteScrollIndex}
            finiteScrollStarts={finiteScrollStarts}
          />
        )}
      </>
    );
  }
);
